import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Select from "react-dropdown-select";

import { Label, TextBox, TextArea, DatePiker } from "modules/primitives";
import styles from "./styles.module.scss";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";
import PlantSingleSelect from "../../../../common/components/plantSingleselect";
import UserMultiSelectSearch from "../../../../common/components/usersSearch";

const PrintingSavings = ({ formikprops, proposalData }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const {
    adminPlantsAndAreasReducer: { plantsAndAreas },
  } = useSelector((state) => state);

  useEffect(() => {
    if (!plantsAndAreas.length) {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        }),
      );
    }
  }, [plantsAndAreas]);

  const getPlantsAsOptions = useCallback((filterBy) => {
    const makePlants = plantsAndAreas.map(plant => ({
      id: plant.id,
      name: plant.attributes.name,
      areas: plant.attributes.areas,
    }));
    const { values } = formikprops;
    const existingValue = values[filterBy];

    if (filterBy === 'selectedPlant' && existingValue?.id) {
      return makePlants.filter(plant => existingValue.id !== plant.id);
    }

    if (filterBy === 'selectedPlant1' && existingValue?.length) {
      return makePlants.filter(plant => !existingValue.some(p => p.id === plant.id));
    }

    return makePlants;
  }, [plantsAndAreas, formikprops.values]);

  const getFundedBy = useMemo(() => {
    if (!proposalData) {
      return null;
    }

    const field = proposalData?.proposal_for_prototype?.find(f => f.field_name === 'Prototype Funded By' || f.field_name === 'Funded By' || f.field_name === 'Implementation Funded By');
    const formattedOptions = field?.option_values?.map((option) => ({
      label: option,
      value: option,
    })) || [];
    const values = formikprops.values?.funded_by?.length
      ? formikprops.values?.funded_by
        .map(elem => formattedOptions.find(el => el.value === (elem?.label || elem)))
      : [];

    return (
      <div className="mb-3">
        <Label>Implementation Funded By</Label>
        <Select
          name="funded_by"
          multi={field?.multiselect}
          options={formattedOptions}
          onChange={(val) => {
            formikprops.setFieldValue('funded_by', val);
          }}
          values={values}
        />
      </div>
    )
  }, [proposalData, formikprops]);

  const handleChange = (val, prop) => {
    if (prop === "tarr_percentage" || prop === 'investment_usd') {
      let savings_usd;

      if (!val?.length || !Number(val)) {
        savings_usd = null;
      } else {
        if (prop === "tarr_percentage") {
          if (formikprops.values?.investment_usd !== 0) {
            const calculated = (val / 100) * formikprops.values?.investment_usd;
            savings_usd = calculated.toFixed(0);
          } else {
            savings_usd = null;
          }
        } else {
          if (Number(formikprops.values?.tarr_percentage)) {
            const calculatedValue = val * (formikprops.values?.tarr_percentage / 100);
            savings_usd = calculatedValue.toFixed(0);
          } else {
            savings_usd = null;
          }
        }
      }

      formikprops.setFieldValue("savings_usd", savings_usd);
      formikprops.setFieldValue(prop, val);

      return;
    }

    if (prop === 'savings_usd') {
      const tarr_percentage = val / formikprops.values?.investment_usd;

      formikprops.setFieldValue("tarr_percentage", (Math.round(tarr_percentage * 100) / 100) * 100);
      formikprops.setFieldValue(prop, val);

      return;
    }

    formikprops.setFieldValue(prop, val);
  };

  return (
    <div className="d-flex flex-column w-100">
      <div className="mb-2">
        <Label>Description</Label>
        <TextArea
          type="text"
          name="description"
          placeholder="Describe the problem you are trying to solve, the project goal, or description."
          className="w-100"
          formProps={formikprops}
          rows={4}
        />
      </div>
      <div className="mb-3">
        <PlantSingleSelect formikprops={formikprops} plants={getPlantsAsOptions("selectedPlant1")} />
        {
          !formikprops?.values?.selectedPlant?.id && (
            <div className={styles.invalidFeedback}>Plant is required</div>
          )
        }
      </div>
      {getFundedBy}
      <div className="mb-3">
        <Label>Investment ($)</Label>
        <TextBox
          name="investment_usd"
          placeholder="Investment ($)"
          className="w-100"
          value={formikprops.values?.investment_usd}
          onChange={(e) => {
            handleChange(e.target.value, "investment_usd");
          }}
          formProps={formikprops}
        />
      </div>
      <div className="mb-3">
        <Label>TARR (%)</Label>
        <TextBox
          type="number"
          placeholder="TARR (%)"
          className="w-100"
          value={formikprops.values.tarr_percentage}
          onChange={(e) =>
            handleChange(e.target.value, "tarr_percentage")
          }
          min={0}
        />
      </div>
      <div className="mb-3">
        <Label>Savings ($)</Label>
        <TextBox
          type="number"
          placeholder="Savings ($)"
          className="w-100"
          value={formikprops.values.savings_usd}
          onChange={(e) =>
            handleChange(e.target.value, "savings_usd")
          }
        />
      </div>
      <div className="mb-3">
        <Label>Job 1 Date</Label>
        <DatePiker
          name="job_1_date"
          value={formikprops.values.job_1_date}
          onChange={(val) => formikprops.setFieldValue("job_1_date", val)}
          showTime
          withClear
        />
      </div>
      <div className="mb-3">
        <Label>Select Project Owner</Label>
        <UserMultiSelectSearch
          multi={false}
          placeholder="Search for project owner"
          labelField="full_name"
          valueField="id"
          searchBy="full_name"
          hasAll={false}
          onChange={(value) => {
            formikprops.setFieldValue("project_owners", value);
          }}
          preselectCurrentUser
        />
        {formikprops.errors["project_owners"] && (
          <div className={styles.invalidFeedback}>{formikprops.errors['project_owners']}</div>
        )}
      </div>
    </div>
  );
};

export default PrintingSavings;
